import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import SlideOver from "@/components/SlideOver";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import Onboarding from "@/components/Onboarding";
import ConciergeDrawer from "@/components/ConciergeDrawer";
import ProfileAvatar from "@/components/ProfileAvatar";
import FounderBadge from "@/components/FounderBadge";
import WalletTopUpModal from "@/components/WalletTopUpModal";
import DeleteAccountModal from "@/components/DeleteAccountModal";
import PayoutManager from "@/components/PayoutManager";
import { isPremiumUnlocked as isPremiumUnlockedHelper, isBusinessActive as isBusinessActiveHelper } from "@/lib/upgrade";
import { LogOut, Crown, Check, Mail, MessageSquare, Smartphone, Coins, CreditCard, ExternalLink, Loader2, Menu, Sparkle, Upload, Trash2, Image as ImageIcon, Plus, Wallet, AlertTriangle } from "lucide-react";

const Toggle = ({ on, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${on ? "bg-secondary" : "bg-muted"}`}
  >
    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

const AppShell = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("gifted_sidebar_collapsed") === "1"
  );
  const [prefs, setPrefs] = useState({ email_marketing: false, sms_marketing: false });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const isRootAdmin = !!user?.is_root_admin;
  const [vaultEntries, setVaultEntries] = useState(null);
  const [addingVault, setAddingVault] = useState(false);
  const [vaultForm, setVaultForm] = useState({ title: "", kind: "note", content: "", image_base64: "" });
  const [savingVault, setSavingVault] = useState(false);
  // Header wallet ticker — always one click away from anywhere in the Maison
  const [walletBalance, setWalletBalance] = useState(null);
  const [headerTopupOpen, setHeaderTopupOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);

  const loadWalletTicker = () => {
    api.get("/wallet").then((r) => setWalletBalance(r.data.balance)).catch(() => {});
  };
  useEffect(() => { loadWalletTicker(); }, []);

  useEffect(() => {
    if (settingsOpen) {
      api.get("/credits").then((r) => setCreditInfo(r.data)).catch(() => {});
    }
  }, [settingsOpen]);

  const loadVault = async () => {
    try { const r = await api.get("/admin/maison-vault"); setVaultEntries(r.data.entries || []); }
    catch (e) { setVaultEntries([]); }
  };

  const vaultImageInput = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });
    setVaultForm((f) => ({ ...f, image_base64: b64, kind: f.kind === "note" ? "screenshot" : f.kind }));
  };

  const saveVaultEntry = async () => {
    if (!vaultForm.title.trim()) return;
    setSavingVault(true);
    try {
      await api.post("/admin/maison-vault", vaultForm);
      setVaultForm({ title: "", kind: "note", content: "", image_base64: "" });
      setAddingVault(false);
      await loadVault();
    } finally { setSavingVault(false); }
  };

  const deleteVaultEntry = async (id) => {
    await api.delete(`/admin/maison-vault/${id}`);
    await loadVault();
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/billing/portal");
      if (r.data.url) window.open(r.data.url, "_blank");
      else alert(r.data.note || "Billing portal unavailable.");
    } catch (e) {
      alert(e?.response?.data?.detail || "Transaction Stalled — please try again.");
    } finally { setPortalLoading(false); }
  };

  useEffect(() => {
    localStorage.setItem("gifted_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    if (user?.communication_prefs) {
      setPrefs({
        email_marketing: !!user.communication_prefs.email_marketing,
        sms_marketing: !!user.communication_prefs.sms_marketing,
      });
    }
  }, [user]);

  const savePrefs = async (next) => {
    setPrefs(next);
    setSavingPrefs(true);
    setPrefsSaved(false);
    try {
      await api.put("/settings/communication", next);
      await refreshUser?.();
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 2000);
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [conciergeOpen, setConciergeOpen] = useState(false);
  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  // Directorate Sweep: derive from the shared upgrade.js helpers (single source of truth for
  // the is_root_admin bypass) instead of re-implementing the check locally — avoids drift.
  const isBusinessActive = isBusinessActiveHelper(user);
  const isPremiumUnlocked = isPremiumUnlockedHelper(user);

  return (
    <div className="min-h-screen">
      {/* Desktop fixed rail (lg+) */}
      <Sidebar onOpenSettings={() => setSettingsOpen(true)} collapsed={collapsed} setCollapsed={setCollapsed} />

      {/* Mobile sticky header (<lg) */}
      <header data-testid="mobile-nav-header" className="lg:hidden sticky top-0 z-40 glass border-b border-border/40">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <span className="text-white text-sm font-bold">G</span>
            </div>
            <span className="gifted-brand text-xl">GIFTED</span>
          </div>
          <button data-testid="hamburger-btn" onClick={() => setMobileNavOpen(true)} aria-label="Open menu"
            className="p-2 rounded-xl text-foreground hover:bg-muted/50 transition-colors">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile slide-in drawer (<lg) — 300ms ease-in-out */}
      <div className={`lg:hidden fixed inset-0 z-50 ${mobileNavOpen ? "" : "pointer-events-none"}`} data-testid="mobile-drawer" aria-hidden={!mobileNavOpen}>
        <div
          data-testid="drawer-backdrop"
          onClick={() => setMobileNavOpen(false)}
          className={`absolute inset-0 bg-[hsl(38_30%_12%_/_0.45)] backdrop-blur-sm transition-opacity duration-300 ease-in-out ${mobileNavOpen ? "opacity-100" : "opacity-0"}`}
        />
        <div className={`absolute left-0 top-0 h-full shadow-2xl transition-transform duration-300 ease-in-out ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <Sidebar
            variant="mobile"
            onOpenSettings={() => setSettingsOpen(true)}
            onNavigate={() => setMobileNavOpen(false)}
            onClose={() => setMobileNavOpen(false)}
          />
        </div>
      </div>

      {/* Main content — left margin only on lg+ (reacts to collapse); full-width on mobile */}
      <div className={`transition-all duration-300 min-h-screen flex flex-col ${collapsed ? "lg:ml-[76px]" : "lg:ml-64"}`}>
        {/* Maison Wallet ticker — always one click away, on every page */}
        {walletBalance !== null && (
          <div className="w-full flex items-center justify-end gap-3 px-6 sm:px-10 pt-4" data-testid="header-wallet-ticker">
            <button onClick={() => navigate("/dashboard#wallet")}
              className="flex items-center gap-1.5 text-sm surface rounded-full px-3 py-1.5 hover:border-primary/40" data-testid="header-wallet-balance-btn">
              <Wallet className="w-3.5 h-3.5 gold-text" /> <span className="gold-text font-medium">${walletBalance.toFixed(2)}</span>
            </button>
            <button onClick={() => setHeaderTopupOpen(true)} data-testid="header-add-balance-btn"
              className="flex items-center gap-1 text-xs bg-gradient-to-r from-primary to-secondary text-white rounded-full px-3 py-1.5 hover:opacity-90">
              <Plus className="w-3 h-3" /> Add Balance
            </button>
          </div>
        )}
        <main className="flex-1">
          <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10">{children}</div>
        </main>
        <Footer />
      </div>
      <WalletTopUpModal open={headerTopupOpen} onClose={(shouldRefresh) => { setHeaderTopupOpen(false); if (shouldRefresh) loadWalletTicker(); }} />

      {/* Account / Settings slide-over */}
      <SlideOver open={settingsOpen} onClose={() => setSettingsOpen(false)} title="Account" subtitle="Your concierge membership & preferences">
        <div className="space-y-6">
          <div className="surface rounded-3xl p-6 flex items-center gap-4">
            <ProfileAvatar name={user?.name} sizeClass="w-14 h-14" textClass="text-xl" isRootAdmin={isRootAdmin} />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-serif text-xl">{user?.name}</div>
                {isRootAdmin && <FounderBadge size="xs" />}
              </div>
              <div className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> {user?.email}
              </div>
            </div>
          </div>

          <div className="surface rounded-3xl p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 gold-text" />
                <span className="font-medium">Billing &amp; Plan</span>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full border ${isPremiumUnlocked || isBusinessActive ? "bg-secondary/15 text-secondary border-secondary/30" : "bg-muted/40 text-muted-foreground border-border"}`}>
                {isBusinessActive ? "Business Team" : isPremiumUnlocked ? "Premium" : "Boutique"}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mb-3" data-testid="billing-credits">
              <span className="text-muted-foreground flex items-center gap-1.5"><Coins className="w-4 h-4 gold-text" /> Mailing credits</span>
              <span className="gold-text font-medium">{creditInfo === null ? "…" : creditInfo.mailing_credits}</span>
            </div>
            {isPremiumUnlocked || isBusinessActive ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2 mb-3">
                <Check className="w-4 h-4 text-secondary" /> {isBusinessActive ? "Business Team features unlocked." : "Premium features unlocked."}
              </p>
            ) : (
              <button onClick={() => { setSettingsOpen(false); navigate("/subscription"); }}
                className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-3 hover:opacity-90 mb-3">
                Buy a Credit Pack
              </button>
            )}
            <button data-testid="billing-portal-btn" onClick={openPortal} disabled={portalLoading}
              className="w-full flex items-center justify-center gap-2 surface rounded-2xl py-2.5 text-primary hover:border-primary/40 disabled:opacity-60">
              {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Manage billing &amp; receipts <ExternalLink className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Communication Preferences */}
          <div className="surface rounded-3xl p-6" data-testid="comm-prefs">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">Communication Preferences</span>
              {prefsSaved && <span className="text-xs text-secondary">Saved</span>}
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-4 h-4 text-primary" />
                  <div>
                    <div className="text-sm">Email marketing</div>
                    <div className="text-xs text-muted-foreground">Occasion tips & seasonal ideas</div>
                  </div>
                </div>
                <Toggle testId="pref-email" on={prefs.email_marketing} onClick={() => !savingPrefs && savePrefs({ ...prefs, email_marketing: !prefs.email_marketing })} />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-4 h-4 text-primary" />
                  <div>
                    <div className="text-sm">SMS marketing</div>
                    <div className="text-xs text-muted-foreground">Gentle reminders before milestones</div>
                  </div>
                </div>
                <Toggle testId="pref-sms" on={prefs.sms_marketing} onClick={() => !savingPrefs && savePrefs({ ...prefs, sms_marketing: !prefs.sms_marketing })} />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground/70 mt-4">You can opt out anytime. Transactional messages (delivery updates) are always sent.</p>
          </div>

          {/* Cash Gift Payout — manage your connected bank account or debit card any time:
              add, remove, or update, independent of any specific gift. */}
          <div className="surface rounded-3xl p-6" data-testid="payout-settings">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="w-5 h-5 gold-text" />
              <span className="font-medium">Payout Method</span>
            </div>
            <PayoutManager compact balance={walletBalance || 0} onCashedOut={loadWalletTicker} />
          </div>

          {/* Maison Evolution — Director's Vault (Root Admin only) */}
          {isRootAdmin && (
            <div className="surface rounded-3xl p-6" data-testid="maison-evolution">
              <div className="flex items-center gap-2 mb-2">
                <Sparkle className="w-5 h-5 gold-text" />
                <span className="font-medium">Maison Evolution</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/10 gold-text border border-yellow-500/20 ml-auto">Root · Encrypted</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Encrypted archive of build blueprints, logic summaries &amp; original design screenshots — visible only to you.</p>
              {vaultEntries === null ? (
                <button data-testid="open-vault-btn" onClick={loadVault}
                  className="w-full surface rounded-2xl py-2.5 text-sm text-primary hover:border-primary/40">Open the Vault</button>
              ) : (
                <>
                  <div className="space-y-2 max-h-64 overflow-y-auto mb-3" data-testid="vault-entries">
                    {vaultEntries.length === 0 ? <p className="text-sm text-muted-foreground">Vault empty.</p> :
                      vaultEntries.map((e) => (
                        <details key={e.id} className="surface rounded-2xl p-3">
                          <summary className="text-sm font-medium cursor-pointer flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wider gold-text">{e.kind}</span> {e.title}
                            <button onClick={(ev) => { ev.preventDefault(); deleteVaultEntry(e.id); }} className="ml-auto text-muted-foreground hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                          </summary>
                          {e.image_base64 && <img src={`data:image/png;base64,${e.image_base64}`} alt="" className="w-full rounded-xl mt-2" />}
                          {e.content && <pre className="text-[11px] text-muted-foreground whitespace-pre-wrap mt-2 max-h-56 overflow-y-auto">{e.content}</pre>}
                        </details>
                      ))}
                  </div>
                  {!addingVault ? (
                    <button data-testid="add-vault-entry-btn" onClick={() => setAddingVault(true)}
                      className="w-full surface rounded-2xl py-2.5 text-sm text-primary hover:border-primary/40">+ Archive a blueprint / screenshot</button>
                  ) : (
                    <div className="surface rounded-2xl p-3 space-y-2" data-testid="vault-entry-form">
                      <input data-testid="vault-title" value={vaultForm.title} onChange={(e) => setVaultForm({ ...vaultForm, title: e.target.value })}
                        className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="Title" />
                      <select value={vaultForm.kind} onChange={(e) => setVaultForm({ ...vaultForm, kind: e.target.value })}
                        className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40">
                        <option value="note">Note</option>
                        <option value="blueprint">Blueprint</option>
                        <option value="screenshot">Screenshot</option>
                      </select>
                      <textarea data-testid="vault-content" value={vaultForm.content} onChange={(e) => setVaultForm({ ...vaultForm, content: e.target.value })} rows={3}
                        className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 resize-none" placeholder="Logic summary / notes (optional)" />
                      <label className="w-full flex items-center justify-center gap-1.5 text-xs surface rounded-lg py-2 cursor-pointer hover:border-primary/40">
                        <Upload className="w-3.5 h-3.5" /> {vaultForm.image_base64 ? "Screenshot attached ✓" : "Attach a screenshot (optional)"}
                        <input data-testid="vault-image-input" type="file" accept="image/*" className="hidden" onChange={vaultImageInput} />
                      </label>
                      {vaultForm.image_base64 && <img src={`data:image/png;base64,${vaultForm.image_base64}`} alt="" className="w-full rounded-xl" />}
                      <div className="flex gap-2">
                        <button data-testid="save-vault-entry-btn" onClick={saveVaultEntry} disabled={savingVault}
                          className="flex-1 text-sm bg-gradient-to-r from-secondary to-primary text-white rounded-lg py-2 hover:opacity-90 disabled:opacity-60">{savingVault ? "Archiving…" : "Archive"}</button>
                        <button onClick={() => setAddingVault(false)} className="flex-1 text-sm surface rounded-lg py-2 text-muted-foreground">Cancel</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <button data-testid="logout-btn" onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 surface rounded-2xl py-3.5 text-red-500 hover:border-red-400/40 transition-colors">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>

          {/* Danger Zone — prominent, always visible Account Deletion (App Store 5.1.1(v)) */}
          {!isRootAdmin && (
            <div className="rounded-3xl p-6 border-2 border-red-500/30 bg-red-500/5" data-testid="danger-zone">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="font-medium text-red-500">Danger Zone</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Permanently delete your account and all associated data — Loved Ones, cards, and Maison Wallet history. This cannot be undone.</p>
              <button data-testid="delete-account-btn" onClick={() => setDeleteAccountOpen(true)}
                className="w-full flex items-center justify-center gap-2 bg-red-600 text-white font-medium rounded-2xl py-3 hover:opacity-90 transition-opacity">
                <Trash2 className="w-4 h-4" /> Delete My Account
              </button>
            </div>
          )}
        </div>
      </SlideOver>

      <DeleteAccountModal open={deleteAccountOpen} onClose={() => setDeleteAccountOpen(false)} />

      {/* Vision Concierge floating launcher (hidden while its drawer is open) */}
      {!conciergeOpen && (
        <button data-testid="concierge-launcher" onClick={() => setConciergeOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform gold-pulse-ring"
          title="Vision Concierge">
          <Sparkle className="w-6 h-6" />
        </button>
      )}
      <ConciergeDrawer open={conciergeOpen} onClose={() => setConciergeOpen(false)} />

      {/* First-run concierge onboarding */}
      <Onboarding />

      <CookieConsent />
    </div>
  );
};

export default AppShell;
